import { useEffect, useRef } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import {
  auth,
  db,
  doc,
  updateDoc,
  arrayUnion,
  getFirebaseMessaging,
} from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

/**
 * Registers FCM device token on `users/{uid}.fcmTokens` and shows foreground notifications.
 */
export function useFcm(user) {
  const warnedRef = useRef(false);

  useEffect(() => {
    const inBrowser = typeof window !== 'undefined';
    const canPush =
      inBrowser &&
      (window.location.protocol === 'https:' || window.location.hostname === 'localhost');
    if (!user?.uid || !canPush) return;

    let cancelled = false;

    (async () => {
      try {
        const messaging = await getFirebaseMessaging();
        if (!messaging || cancelled) return;

        if (!VAPID_KEY && !warnedRef.current) {
          warnedRef.current = true;
          // eslint-disable-next-line no-console
          console.warn('[Haydi] Set REACT_APP_FIREBASE_VAPID_KEY for web push (Firebase Console → Project settings → Cloud Messaging).');
          return;
        }
        if (!VAPID_KEY) return;

        const permission = await Notification.requestPermission();
        if (permission !== 'granted' || cancelled) return;

        const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });

        if (!token || cancelled) return;
        if (auth.currentUser?.uid !== user.uid) return;

        await updateDoc(doc(db, 'users', user.uid), {
          fcmTokens: arrayUnion(token),
        });

        onMessage(messaging, (payload) => {
          const title = payload.notification?.title || payload.data?.title || 'Haydi';
          const body = payload.notification?.body || payload.data?.body || '';
          if (document.visibilityState === 'visible' && body) {
            try {
              // eslint-disable-next-line no-new
              new Notification(title, { body, icon: '/logo192.png' });
            } catch (e) {
              /* ignore */
            }
          }
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Haydi] FCM setup skipped:', e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);
}
